import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, DollarSign, Coins, Heart, Loader2 } from 'lucide-react';

const PRESET_AMOUNTS_USD = [1, 5, 10, 25, 50, 100];
const PRESET_AMOUNTS_TOKENS = [10, 25, 50, 100, 250, 500];

export default function RewardModal({
  isOpen,
  onClose,
  recipientName = 'Host',
  recipientEmail,
  roomId,
  userTokenBalance = 0,
  onSendReward
}) {
  const [currency, setCurrency] = useState('usd');
  const [amount, setAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const presetAmounts = currency === 'usd' ? PRESET_AMOUNTS_USD : PRESET_AMOUNTS_TOKENS;

  const handleSendReward = async () => {
    if (!amount || amount <= 0) return;

    // Check token balance
    if (currency === 'rally_tokens' && amount > userTokenBalance) {
      alert(`Insufficient Rally tokens. You have ${userTokenBalance} tokens.`);
      return;
    }

    setIsProcessing(true);
    try {
      // Call the onSendReward callback if provided
      if (onSendReward) {
        await onSendReward({
          recipientEmail,
          recipientName,
          roomId,
          amount,
          currency,
          message: message.trim()
        });
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        // Reset form
        setAmount(0);
        setCustomAmount('');
        setMessage('');
      }, 2000);
    } catch (error) {
      console.error('Failed to send reward:', error);
      alert('Failed to send reward. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setAmount(0);
    setCustomAmount('');
    setMessage('');
    setCurrency('usd');
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Modal */}
      <div className="relative bg-[#001233] border border-white/20 rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden animate-fadeIn">
        {/* Success Animation */}
        {showSuccess && (
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center z-10">
            <div className="text-center">
              <Heart className="w-16 h-16 text-green-400 mx-auto mb-2 fill-current" />
              <p className="text-white font-semibold text-lg">Reward Sent!</p>
              <p className="text-gray-300 text-sm">Thank you for your support</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold text-white">Send Reward</h2>
            <p className="text-xs text-gray-400">Support {recipientName}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Currency Selection */}
          <div>
            <label className="text-gray-400 text-xs mb-2 block">Choose Currency</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setCurrency('usd');
                  setAmount(0);
                  setCustomAmount('');
                }}
                className={`p-3 rounded-xl border-2 transition-all ${
                  currency === 'usd'
                    ? 'bg-green-500/20 border-green-500 text-white'
                    : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                }`}
              >
                <DollarSign className="w-5 h-5 mx-auto mb-0.5" />
                <span className="text-xs font-medium">USD</span>
              </button>
              <button
                onClick={() => {
                  setCurrency('rally_tokens');
                  setAmount(0);
                  setCustomAmount('');
                }}
                className={`p-3 rounded-xl border-2 transition-all ${
                  currency === 'rally_tokens'
                    ? 'bg-purple-500/20 border-purple-500 text-white'
                    : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                }`}
              >
                <Coins className="w-5 h-5 mx-auto mb-0.5" />
                <span className="text-xs font-medium">Rally Tokens</span>
              </button>
            </div>
            {currency === 'rally_tokens' && (
              <p className="text-xs text-gray-500 mt-2">
                Your balance: <span className="text-purple-400 font-medium">{userTokenBalance} tokens</span>
              </p>
            )}
          </div>

          {/* Preset Amounts */}
          <div>
            <label className="text-gray-400 text-xs mb-2 block">Select Amount</label>
            <div className="grid grid-cols-3 gap-2">
              {presetAmounts.map((preset) => (
                <button
                  key={preset}
                  onClick={() => {
                    setAmount(preset);
                    setCustomAmount('');
                  }}
                  className={`p-2 rounded-lg transition-all border ${
                    amount === preset
                      ? 'bg-[#6a1fbf] text-white border-[#6a1fbf]'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10 border-white/10'
                  }`}
                >
                  <span className="text-xs font-semibold">
                    {currency === 'usd' ? `$${preset}` : `${preset}`}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount */}
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">Custom Amount</label>
            <input
              type="number"
              min="1"
              value={customAmount}
              onChange={(e) => {
                setCustomAmount(e.target.value);
                setAmount(parseFloat(e.target.value) || 0);
              }}
              placeholder={currency === 'usd' ? '0.00' : '0'}
              className="w-full bg-white/5 border border-white/10 text-white text-base h-9 rounded-lg px-3 focus:outline-none focus:border-[#6a1fbf] transition-colors"
            />
          </div>

          {/* Optional Message */}
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">Message (Optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Great content!"
              className="w-full bg-white/5 border border-white/10 text-white resize-none text-sm rounded-lg p-3 focus:outline-none focus:border-[#6a1fbf] transition-colors"
              rows={2}
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">{message.length}/200</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-white/5 border-t border-white/10 flex gap-2">
          <button
            onClick={handleClose}
            className="flex-1 py-2 px-4 bg-transparent border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSendReward}
            disabled={!amount || amount <= 0 || isProcessing}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
              !amount || amount <= 0 || isProcessing
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white hover:opacity-90'
            }`}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Send {currency === 'usd' ? `$${amount}` : `${amount} tokens`}
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>,
    document.body
  );
}
